import { formatCurrency, formatDate } from '@crm/shared';
import { Badge, Card, CardContent, cn, Input, Spinner } from '@crm/ui';
import {
  Building2,
  ChevronRight,
  CircleAlert,
  CircleDollarSign,
  Clock3,
  MessageSquare,
  Power,
  PowerOff,
  Users,
  Wallet,
} from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  EmptyState,
  type FilterPill,
  FilterPills,
  PageHeader,
  Pagination,
  StatCard,
  WIDGET_GRID,
} from '@/components';
import { useDocumentTitle } from '@/hooks';
import {
  SAAS_ROUTES,
  type Subscriber,
  type SubscriberStatus,
  useSubscribers,
  useSubscribersOverview,
} from '../api';

const STATUS_OPTIONS: FilterPill<SubscriberStatus>[] = [
  { key: 'all', label: 'Todos', icon: Building2 },
  { key: 'active', label: 'Ativos', icon: Power },
  { key: 'inactive', label: 'Inativos', icon: PowerOff },
  { key: 'waitlist', label: 'Fila de espera', icon: Clock3 },
];

function Numero({ icon, value, label }: { icon: React.ReactNode; value: string; label: string }) {
  return (
    <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
      {icon}
      <span className="font-medium text-foreground">{value}</span>
      {label}
    </span>
  );
}

function Widgets() {
  const overview = useSubscribersOverview();
  const dados = overview.data;

  if (!dados) return null;

  return (
    <div className={WIDGET_GRID.seven}>
      <StatCard
        title="Assinantes"
        value={dados.total}
        hint={`${dados.whatsappOnline} com WhatsApp no ar`}
        icon={<Building2 />}
      />
      <StatCard title="Ativos" value={dados.active} icon={<Power />} />
      <StatCard title="Inativos" value={dados.inactive} icon={<PowerOff />} />
      <StatCard title="Free" value={dados.withoutPlan} hint="sem cobranca" icon={<CircleAlert />} />
      <StatCard
        title="Receita mensal"
        shortTitle="Receita"
        value={formatCurrency(dados.monthlyRevenue)}
        hint="soma dos planos ativos"
        icon={<CircleDollarSign />}
      />
      <StatCard
        title="Creditos em caixa"
        shortTitle="Creditos"
        value={formatCurrency(dados.creditBalance)}
        icon={<Wallet />}
      />
      <StatCard
        title="Fila de espera"
        shortTitle="Fila"
        value={dados.waitlist}
        hint="aguardando liberacao"
        icon={<Clock3 />}
      />
    </div>
  );
}

function SubscriberRow({ subscriber }: { subscriber: Subscriber }) {
  return (
    <li>
      <Link
        to={SAAS_ROUTES.subscriber(subscriber.id)}
        className="flex items-center gap-3 rounded-lg border border-border p-3 transition-colors hover:bg-muted/60"
      >
        <span
          className={cn(
            'flex h-10 w-10 shrink-0 items-center justify-center rounded-xl',
            subscriber.isActive ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground',
          )}
        >
          <Building2 className="h-5 w-5" />
        </span>

        <div className="min-w-0 flex-1 space-y-1">
          <div className="flex flex-wrap items-center gap-2">
            <p className="truncate font-medium">{subscriber.name}</p>
            {subscriber.isActive ? null : (
              <Badge variant="outline" className="border-destructive/50 text-destructive">
                Inativo
              </Badge>
            )}
            {subscriber.onWaitlist ? (
              <Badge
                variant="outline"
                className="gap-1 border-amber-500/50 text-amber-700 dark:text-amber-400"
              >
                <Clock3 className="h-3 w-3" />
                Fila de espera
              </Badge>
            ) : null}
            {subscriber.whatsappUnits > 0 ? (
              <Badge
                variant="outline"
                className="border-emerald-500/50 text-emerald-700 dark:text-emerald-400"
              >
                WhatsApp no ar
              </Badge>
            ) : null}
            <Badge variant="secondary">{subscriber.planName}</Badge>
          </div>

          <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
            <Numero
              icon={<Building2 className="h-3.5 w-3.5" />}
              value={String(subscriber.unitsCount)}
              label="unidades"
            />
            <Numero
              icon={<Users className="h-3.5 w-3.5" />}
              value={String(subscriber.usersCount)}
              label="usuarios"
            />
            <Numero
              icon={<MessageSquare className="h-3.5 w-3.5" />}
              value={String(subscriber.customersCount)}
              label="clientes"
            />
            <Numero
              icon={<Wallet className="h-3.5 w-3.5" />}
              value={formatCurrency(subscriber.creditBalance)}
              label="em credito"
            />
          </div>

          <p className="text-xs text-muted-foreground">
            {subscriber.document ? `${subscriber.document} - ` : ''}
            assinante desde {formatDate(subscriber.createdAt)}
          </p>
        </div>

        <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
      </Link>
    </li>
  );
}

export function SubscribersPage() {
  const overview = useSubscribersOverview();
  useDocumentTitle('Assinantes');

  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<SubscriberStatus>('all');
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(20);

  const subscribers = useSubscribers({ search, status, page, perPage });
  const lista = subscribers.data?.data ?? [];

  return (
    <div className="space-y-4">
      <PageHeader
        icon={<Building2 />}
        title="Assinantes"
        description="Os saloes que usam o sistema."
      />

      <Widgets />

      <Input
        placeholder="Buscar por nome, razao social ou documento..."
        value={search}
        onChange={(event) => {
          setSearch(event.target.value);
          setPage(1);
        }}
      />

      <FilterPills
        options={STATUS_OPTIONS.map((opcao) => ({
          ...opcao,
          count:
            opcao.key === 'all'
              ? overview.data?.total
              : opcao.key === 'active'
                ? overview.data?.active
                : opcao.key === 'inactive'
                  ? overview.data?.inactive
                  : overview.data?.waitlist,
        }))}
        value={status}
        onChange={(proximo) => {
          setStatus(proximo);
          setPage(1);
        }}
      />

      <Card variant="flush">
        <CardContent className="space-y-3 py-4">
          {subscribers.isLoading ? (
            <div className="flex justify-center py-8">
              <Spinner className="h-6 w-6 text-muted-foreground" />
            </div>
          ) : lista.length === 0 ? (
            <EmptyState icon={<Building2 />} title="Nenhum assinante encontrado" />
          ) : (
            <ul className="space-y-2">
              {lista.map((subscriber) => (
                <SubscriberRow key={subscriber.id} subscriber={subscriber} />
              ))}
            </ul>
          )}

          <Pagination
            meta={subscribers.data?.meta}
            label="assinantes"
            onChange={setPage}
            perPage={perPage}
            onPerPageChange={(proximo) => {
              setPerPage(proximo);
              setPage(1);
            }}
          />
        </CardContent>
      </Card>
    </div>
  );
}
